import { useState } from "react";
import DOMPurify from "dompurify";
import { format } from "date-fns";

import { Button } from "components/button/Button";
import { Icon } from "components/icon/Icon";
import Modal from "components/modal/Modal";
import Persona from "components/persona/Persona";
import { Attachment } from "models/dashboard/dashboard";
import { Thread, MessageItem } from "models/inbox/inbox";
import { sv } from "date-fns/locale";

import "./Message.scss";

type Props = {
  canDeleteMessage?: boolean;
  message: MessageItem;
  sender: {
    firstName: string;
    lastName: string;
  };
  onOpenDocument: (item: { id: string; name: string }) => void;
  showRecipients?: boolean;
  thread?: Thread;
  onDeleteMessage?: (messageId: string, messageType: string) => void;
};

export default function Message({
  canDeleteMessage,
  message,
  sender,
  thread,
  onDeleteMessage,
  onOpenDocument,
  showRecipients
}: Props) {
  const [verifyRemoveMsg, setVerifyRemoveMsg] = useState(false);

  const handleVerifyDeleteMessage = () => {
    const { messageId, type } = message;
    if (onDeleteMessage) {
      onDeleteMessage(messageId, type);
    }

    setVerifyRemoveMsg(false);
  };

  const handleOnClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    attachment: Attachment
  ) => {
    e.preventDefault();
    onOpenDocument(attachment);
  };

  const createMarkup = () => {
    return { __html: DOMPurify.sanitize(message.body) };
  };

  const timestamp = format(new Date(message.createdAt), "HH:mm");

  const date = format(new Date(message.createdAt), "d LLLL yyyy", {
    locale: sv
  });

  return (
    <>
      {verifyRemoveMsg && (
        <Modal
          header={<h1 className="m-0">Radera meddelande</h1>}
          onDismiss={() => setVerifyRemoveMsg(false)}
          modalTitle="delete message"
          testId="messages-modal"
          footer={
            <>
              <Button
                color={"tertiary"}
                className={"verifyButton mr-3 mb-4"}
                data-testid="confirm-button"
                onClick={handleVerifyDeleteMessage}
              >
                Ja, radera meddelande
              </Button>
              <Button
                color={"outline"}
                className={"cancelButton"}
                data-testid="cancel-button"
                onClick={() => setVerifyRemoveMsg(false)}
              >
                Avbryt
              </Button>
            </>
          }
        >
          <div className="modalText">
            <p>Vill du radera nedanstående meddelande?</p>
            <div className="messageBody">
              {thread !== undefined && (
                <div>
                  <span className="sender">
                    {sender.firstName} {sender.lastName}
                  </span>
                </div>
              )}

              <div dangerouslySetInnerHTML={createMarkup()} />
            </div>
          </div>
        </Modal>
      )}

      <div className="message">
        <div className="d-md-flex">
          <Persona
            firstName={sender.firstName}
            lastName={sender.lastName}
            className="mr-4 d-sm-none d-md-flex"
          />

          <div className="flex-1-1">
            <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
              <div className="sender text-nowrap">
                <div className="mr-2">Från: </div>
                <div className="text-nowrap">
                  {sender.firstName} {sender.lastName}
                </div>
              </div>

              {showRecipients && message.subscribers && (
                <div className="sender text-nowrap">
                  <div className="mr-2">Till: </div>
                  <div className="text-nowrap">
                    {message.subscribers.map((s, i, arr) => (
                      <span className="mr-2">
                        {`${s.givenName} ${s.surName}`}
                        {i !== arr.length - 1 && ", "}
                      </span>
                    ))}

                    {message.subscribers.length > 5 && (
                      <> + {message.subscribers.length - 5} andra mottagare</>
                    )}
                  </div>
                </div>
              )}

              <div className="my-3">
                <time className="text-gray">
                  {date} &#8212; {timestamp}
                </time>
              </div>
            </div>

            <div
              className="messageContent"
              dangerouslySetInnerHTML={createMarkup()}
            />

            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-4">
                {message.attachments.map((attachment) => {
                  return (
                    <div
                      key={attachment.id}
                      className="attachment"
                      onClick={(e) => handleOnClick(e, attachment)}
                    >
                      <Icon name="paperclip" className="mr-2" />
                    <span className="attachment-name">{attachment.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="metaWrapper">
          {canDeleteMessage && (
            <Button
              size="sm"
              className="no-print deleteButton mt-4"
              color="tertiary"
              variant="outline"
              data-testid="delete-button"
              onClick={() => setVerifyRemoveMsg(true)}
            >
              <Icon name="trash" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
